/**
 * **A agenda de enderecos do titular** — `API §6.2`, `CONTRACTS §8.2` (bloco "Perfil e conta").
 *
 * Quatro operacoes, duas linhas de rota: `GET|POST /v1/me/addresses` e
 * `PATCH|DELETE /v1/me/addresses/{id}`. Todas `SES`, todas `M`.
 *
 * ## Por que este arquivo bloqueava a entrega inteira
 *
 * `QuoteRequestSchema` ja discrimina a modalidade — `{ mode: 'DELIVERY', addressId }` — e o
 * servidor ja resolve zona, frete e ETA a partir dela (`findZoneForAddress`), ja congela o
 * endereco no pedido (`deliveryStreet`, `deliveryReference`, ...) e ja o imprime no ticket. O
 * unico elo que faltava era o cliente **criar** um endereco. Sem estas rotas, `DELIVERY` era
 * inalcancavel pela tela e todo pedido de verdade nascia `PICKUP`.
 *
 * ## `reference` e obrigatorio, e a recusa tem codigo proprio
 *
 * R-28, e o motivo esta na `CepSchema`: *"ha municipio com CEP unico"*. No interior o endereco
 * postal nao leva ninguem a lugar nenhum — quem leva e *"em frente a praca, portao azul"*. Por
 * isso `reference` e a unica linha de endereco que o produto exige, e por isso a recusa tem
 * codigo proprio (`ADDRESS_REFERENCE_REQUIRED`, 422) em vez de cair no `VALIDATION_ERROR`
 * generico: a acao sugerida e `FIELD`, e o app precisa saber em qual campo pousar o cursor.
 *
 * **Onde a exigencia mora:** no caso de uso, nao neste schema. `reference: z.string()` aqui
 * aceita string vazia de proposito — se o `min(1)` estivesse aqui, o Zod reprovaria primeiro e
 * `ADDRESS_REFERENCE_REQUIRED` seria codigo morto no catalogo. Campo **ausente** continua sendo
 * `VALIDATION_ERROR` com ponteiro `/reference`, e a distincao e real: ausente e corpo
 * malformado (nao veio do nosso app); vazio e uma pessoa que deixou o campo em branco.
 *
 * ## O que NAO esta aqui
 *
 * | Fora do contrato | Por que |
 * | --- | --- |
 * | `lat` / `lng` | sao PII (`@pii{c:DIRECT}`) e nenhuma tela do MVP as usa; expor coordenada de casa para economizar um round-trip que ninguem faz e o pior negocio possivel |
 * | `isDefault` | `API §6.2` poe `POST /v1/me/addresses/{id}/default` **fora** do MVP e fixa a regra: *"no MVP o default e o endereco mais recente"*. A lista ja vem ordenada por isso, entao um booleano aqui seria uma segunda fonte da mesma verdade — e as duas divergiriam |
 * | `deletedAt` | apagado e inexistente: a lista nao o traz e `PATCH`/`DELETE` nele respondem `404` |
 *
 * ## `zoneId` e opcional, e isso nao e frouxidao
 *
 * A zona e **da filial** (`delivery_zone.storeId`), e o cliente escolhe a filial no checkout —
 * depois de cadastrar o endereco. Exigir `zoneId` na criacao obrigaria a pessoa a escolher a
 * loja antes de dizer onde mora. Endereco sem zona e endereco valido: ele so nao cota frete, e
 * a recusa e `OUT_OF_DELIVERY_AREA` (422, com CTA de retirada), que e o que o
 * `findZoneForAddress` ja devolve para zona inativa.
 */
import { z } from 'zod';

import {
  CepSchema,
  InstantSchema,
  OptimisticVersionSchema,
  PhoneBrE164Schema,
  UfSchema,
  Uuidv7Schema,
} from '../common';

// ---------------------------------------------------------------------------
// Cache
// ---------------------------------------------------------------------------

/**
 * `API §6.2`, X-05: *"endpoint autenticado e sempre `no-store`"*. O `ETag` continua valendo
 * para `304` — o que a borda nao pode e **guardar** a agenda de enderecos de alguem.
 */
export const ADDRESS_CACHE_CONTROL = 'private, no-store';

// ---------------------------------------------------------------------------
// Limites de tamanho
// ---------------------------------------------------------------------------

/**
 * Tetos de campo. Existem por dois motivos, nesta ordem: o ticket termico tem **48 colunas**
 * (`store/domain/ticket.ts`) e uma rua de 400 caracteres vira 9 linhas de papel; e campo de
 * texto livre sem teto e superficie de abuso de armazenamento.
 */
export const ADDRESS_MAX_LABEL = 40;
export const ADDRESS_MAX_STREET = 120;
export const ADDRESS_MAX_NUMBER = 16;
export const ADDRESS_MAX_COMPLEMENT = 80;
export const ADDRESS_MAX_REFERENCE = 160;
export const ADDRESS_MAX_CITY = 80;
export const ADDRESS_MAX_RECIPIENT_NAME = 80;

// ---------------------------------------------------------------------------
// O endereco como o app o le
// ---------------------------------------------------------------------------

export const AddressSchema = z.object({
  id: Uuidv7Schema,
  /**
   * Base do `ETag` fraco `W/"address-<id>-<version>"` (`API §4.1`).
   *
   * **Este campo esteve fora, e a omissao era um defeito.** O raciocinio era "a versao e verdade
   * do servidor, ja exposta pelo `ETag`" — e ele quebra na lista: `GET /v1/me/addresses`
   * responde **um** `ETag`, o da colecao, e nao um por item. Sem `version` no corpo, o app le a
   * agenda e nao tem como montar o `If-Match` de nenhum dos enderecos: `PATCH` e `DELETE`
   * ficariam inalcancaveis pela tela — codigo morto no servidor.
   *
   * `cart` (`CartResponseSchema.version`), `order` e a fila do Painel expoem a versao pela mesma
   * razao. Nao havia motivo para este recurso divergir.
   */
  version: OptimisticVersionSchema,
  /** "Casa", "Trabalho". Livre e opcional — e apelido, nao categoria. */
  label: z.string().max(ADDRESS_MAX_LABEL).nullable(),
  /** `null` = endereco ainda sem zona de entrega. Ver o cabecalho. */
  zoneId: Uuidv7Schema.nullable(),
  street: z.string().min(1).max(ADDRESS_MAX_STREET),
  /** `null` e comum e legitimo: "S/N" tambem e endereco. */
  number: z.string().max(ADDRESS_MAX_NUMBER).nullable(),
  complement: z.string().max(ADDRESS_MAX_COMPLEMENT).nullable(),
  /** R-28 — o campo que o entregador usa de verdade. Nunca vazio na saida. */
  reference: z.string().min(1).max(ADDRESS_MAX_REFERENCE),
  postalCode: CepSchema.nullable(),
  city: z.string().min(1).max(ADDRESS_MAX_CITY),
  uf: UfSchema,
  /**
   * Quem recebe. **Nao** e derivado do cadastro: quem pede pode estar mandando para a mae.
   * E, na pratica, e onde o produto captura um nome — todo cliente de PWA entra por
   * telefone+OTP e comeca sem nenhum (ver `store/domain/customer-label.ts`).
   */
  recipientName: z.string().min(1).max(ADDRESS_MAX_RECIPIENT_NAME),
  recipientPhone: PhoneBrE164Schema,
  createdAt: InstantSchema,
});
export type Address = z.infer<typeof AddressSchema>;

/**
 * `GET /v1/me/addresses`.
 *
 * Sem paginacao: a agenda de uma pessoa tem 1 a 3 enderecos, e um cursor aqui seria protocolo
 * para um caso que nao existe. A ordem e **do mais recente para o mais antigo**, e ela carrega
 * significado — `API §6.2`: *"no MVP o default e o endereco mais recente"*. Portanto
 * `data[0]` **e** o default, e o app nao precisa de um campo para descobrir isso.
 */
export const AddressListResponseSchema = z.object({
  data: z.array(AddressSchema),
});
export type AddressListResponse = z.infer<typeof AddressListResponseSchema>;

// ---------------------------------------------------------------------------
// Entrada
// ---------------------------------------------------------------------------

/**
 * `POST /v1/me/addresses`.
 *
 * `.strict()` como todo corpo do produto: mandar `isDefault` (que a v0 do app tentaria) responde
 * `422` em vez de ser ignorado em silencio.
 *
 * `reference: z.string()` **sem `min(1)`** e deliberado — ver o cabecalho deste arquivo.
 */
export const CreateAddressRequestSchema = z
  .object({
    label: z.string().max(ADDRESS_MAX_LABEL).optional(),
    zoneId: Uuidv7Schema.optional(),
    street: z.string().min(1).max(ADDRESS_MAX_STREET),
    number: z.string().max(ADDRESS_MAX_NUMBER).optional(),
    complement: z.string().max(ADDRESS_MAX_COMPLEMENT).optional(),
    reference: z.string().max(ADDRESS_MAX_REFERENCE),
    postalCode: CepSchema.optional(),
    city: z.string().min(1).max(ADDRESS_MAX_CITY),
    uf: UfSchema,
    recipientName: z.string().min(1).max(ADDRESS_MAX_RECIPIENT_NAME),
    recipientPhone: PhoneBrE164Schema,
  })
  .strict();
export type CreateAddressRequest = z.infer<typeof CreateAddressRequestSchema>;

/**
 * `PATCH /v1/me/addresses/{id}` — exige `If-Match` (`API §4.1`: edicao concorrente app x balcao).
 *
 * Todo campo e opcional, e **`null` e um valor**: `{ complement: null }` apaga o complemento,
 * enquanto omitir o campo o preserva. Sem essa distincao nao existe "apagar o complemento" —
 * mandar `''` gravaria string vazia, que e outra coisa.
 *
 * `.strict()` e um corpo vazio e recusado: `PATCH {}` seria uma escrita que nao escreve nada e
 * mesmo assim consome a versao do `If-Match`.
 */
export const UpdateAddressRequestSchema = z
  .object({
    label: z.string().max(ADDRESS_MAX_LABEL).nullable().optional(),
    zoneId: Uuidv7Schema.nullable().optional(),
    street: z.string().min(1).max(ADDRESS_MAX_STREET).optional(),
    number: z.string().max(ADDRESS_MAX_NUMBER).nullable().optional(),
    complement: z.string().max(ADDRESS_MAX_COMPLEMENT).nullable().optional(),
    reference: z.string().max(ADDRESS_MAX_REFERENCE).optional(),
    postalCode: CepSchema.nullable().optional(),
    city: z.string().min(1).max(ADDRESS_MAX_CITY).optional(),
    uf: UfSchema.optional(),
    recipientName: z.string().min(1).max(ADDRESS_MAX_RECIPIENT_NAME).optional(),
    recipientPhone: PhoneBrE164Schema.optional(),
  })
  .strict()
  .refine((corpo) => Object.keys(corpo).length > 0, {
    message: 'Informe ao menos um campo para alterar.',
  });
export type UpdateAddressRequest = z.infer<typeof UpdateAddressRequestSchema>;
